// ============================================================================
// src/shared/navStripScroll.ts — horizontal-only "reveal the active chip"
// ----------------------------------------------------------------------------
// Shared by both header strips: the green global program strip and the white
// row-2 section strip.
//
// Why not scrollIntoView(): it scrolls EVERY scrollable ancestor, including the
// document. On a phone, landing on a route whose active chip sits off-screen
// would jump the page body vertically before the operator had read anything —
// and with `block: 'nearest'` it still moves the page whenever the header is
// partially scrolled out of view. Writing scrollLeft on the strip element is
// the only motion this helper can produce; the page cannot move.
//
// It also refuses to fight the operator: when the active chip is already fully
// visible, nothing happens, so a mid-swipe scroll position is never yanked back.
//
// Assignment is instantaneous (no smooth behavior), which is inherently
// reduced-motion safe — there is no animation to suppress.
// ============================================================================

// Breathing room so the revealed chip never sits flush against the fade mask.
const EDGE_PAD = 12;

/**
 * Scroll `stripEl` horizontally, and only horizontally, so `activeEl` is fully
 * visible. No-ops when either element is missing or the chip already fits.
 *
 * `stripEl` is the horizontally scrollable strip; `activeEl` is the selected
 * chip inside it. Both call sites read them out of the DOM, where "not found"
 * is null — hence the nullable parameters and the early return.
 *
 * `activeEl` is an `Element`, not an `HTMLElement`, because that is all this
 * function needs: the only thing it ever does with it is read
 * getBoundingClientRect(), which is defined on Element. Requiring HTMLElement
 * was a stronger claim than the body makes, and it forced callers that reach
 * the chip through querySelector() — whose return type is `Element | null` —
 * to either add a type argument or hoist the lookup into a local. Header's
 * subnav call is pinned verbatim by global_nav_static.test.js and can do
 * neither. `stripEl` stays HTMLElement: it is written to (scrollLeft) and is
 * always the ref'd strip element, never a query result.
 */
export function scrollActiveIntoStrip(stripEl: HTMLElement | null, activeEl: Element | null): void {
  if (!stripEl || !activeEl) return;
  if (typeof stripEl.getBoundingClientRect !== 'function') return;
  if (typeof activeEl.getBoundingClientRect !== 'function') return;

  const strip = stripEl.getBoundingClientRect();
  const chip = activeEl.getBoundingClientRect();
  // Nothing to scroll (content fits, or the strip is not laid out yet).
  if (!strip.width) return;

  if (chip.left < strip.left + EDGE_PAD) {
    stripEl.scrollLeft += chip.left - strip.left - EDGE_PAD;
    return;
  }
  if (chip.right > strip.right - EDGE_PAD) {
    stripEl.scrollLeft += chip.right - strip.right + EDGE_PAD;
  }
}
